import { useState, useEffect } from "react";
import {
  IconButton,
  Button
} from "@mui/material";
import { MdVisibility, MdDownload } from "react-icons/md";
import * as XLSX from "xlsx";
import Swal from "sweetalert2";
import { getSelectionViewSummary, getProcessedData } from "../../Api";
import PreviewTable from "../dataprocessing/components/PreviewTable";

const itemsPerPage = 10;

const History = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const themeColor = "var(--theme-color)";
    const [loading, setLoading] = useState(false);
    const [historyRows, setHistoryRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [previewTableData, setPreviewTableData] = useState([]);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);

    const handleViewClick = async (row) => {
        setSelectedFile(row.output_filename || null);
        setSelectedRow(row);
        await fetchPreviewData(row);
    };

    const handleDownloadClick = async (row) => {
        if (!row) return;
        try {
          const bank_code = localStorage.getItem("bank_code");
          const correl_id = localStorage.getItem("correl_id");
          Swal.fire({
            html: `<img src=${require("../../assets/loader.gif")} style="width: 50px" alt="loader">`,
            showConfirmButton: false,
            heightAuto: false,
            customClass: { popup: "loader-swal" },
            allowOutsideClick: false,
          });
          const response = await getProcessedData({
            processed_id: row.processed_id,
            correl_id,
            file_type: row.file_type,
            bank_code,
            output_filename: row.output_filename,
            scenarios: row.scenario ? [row.scenario] : [],
          });
          Swal.close();
          const data = (response && response.status === "S" && response.result_info) ? response.result_info : [];
          if (!data.length) {
            Swal.fire({
              icon: "info",
              text: "No data available to download.",
              showConfirmButton: false,
              timer: 2000,
            });
            return;
          }
          const rows = data.map((r) => ({
            "ENT NUM": r.unique_id || "",
            "Name": r.original_name || "",
            "Pre Processed Name": r.pre_processed_name || "",
            "Processed Name": r.processed_name || "",
            "Scenario": r.scenario || "",
          }));
          const ws = XLSX.utils.json_to_sheet(rows);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "Preview Data");
          XLSX.writeFile(wb, `${row?.output_filename || "preview"}.xlsx`);
        } catch (e) {
          Swal.close();
        }
    };

    const fetchPreviewData = async (row) => {
      if (!row) return;
      setIsPreviewLoading(true);
      try {
        const bank_code = localStorage.getItem("bank_code");
        const correl_id = localStorage.getItem("correl_id");
        Swal.fire({
          html: `<img src=${require("../../assets/loader.gif")} style="width: 50px" alt="loader">`,
          showConfirmButton: false,
          heightAuto: false,
          customClass: { popup: "loader-swal" },
          allowOutsideClick: false,
        });
        const response = await getProcessedData({
          processed_id: row.processed_id,
          correl_id,
          file_type: row.file_type,
          bank_code,
          output_filename: row.output_filename,
          scenarios: row.scenario ? [row.scenario] : [],
        });
        Swal.close();
        if (response && response.status === "S") {
          setPreviewTableData(response.result_info || []);
        } else {
          setPreviewTableData([]);
        }
      } catch (e) {
        Swal.close();
        setPreviewTableData([]);
      } finally {
        setIsPreviewLoading(false);
      }
    };

    const fetchHistory = async (page = 1) => {
      setLoading(true);
      try {
        const bank_code = localStorage.getItem("bank_code");
        const correl_id = localStorage.getItem("correl_id");
        Swal.fire({
          html: `<img src=${require("../../assets/loader.gif")} style="width: 50px" alt="loader">`,
          showConfirmButton: false,
          heightAuto: false,
          customClass: { popup: "loader-swal" },
          allowOutsideClick: false,
        });
        const response = await getSelectionViewSummary({
          bank_code,
          correl_id,
          page,
          pageSize: itemsPerPage,
        });
        Swal.close();
        if (response && response.status === "S") {
          setHistoryRows(response.result_info || []);
          setTotalCount(response.pagination?.totalCount || (response.result_info ? response.result_info.length : 0));
        } else {
          setHistoryRows([]);
          setTotalCount(0);
        }
      } catch (e) {
        Swal.close();
        setHistoryRows([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      fetchHistory(currentPage);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
    const handleFirstPage = () => currentPage !== 1 && setCurrentPage(1);
    const handlePrevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);
    const handleNextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
    const handleLastPage = () => currentPage !== totalPages && setCurrentPage(totalPages);

    return (
        <div className="efficiency-page">
        <div className="page-container">
                <div className="row justify-content-between">
                    <div className="col-12 col-md-4">
                        <h5 className="mb-4">Historical Processed Data</h5>
                    </div>
                </div>
                <div>
                    <table size="small" className="table table-bordered table-striped">
                        <thead className="table-dark">
                        <tr>
                            <th>#</th>
                            <th>File Type</th>
                            <th>File Name</th>
                            <th>Scenario</th>
                            <th>Count</th>
                            <th className="text-center">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="6" className="text-center">Loading...</td>
                          </tr>
                        ) : historyRows && historyRows.length > 0 ? (
                          historyRows.map((row, index) => (
                            <tr key={`${row.output_filename}-${index}`}>
                              <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                              <td>{row.file_type || "-"}</td>
                              <td>{row.output_filename || "-"}</td>
                              <td>{row.scenario || "-"}</td>
                              <td>{row.count ?? "-"}</td>
                              <td className="text-center">
                                <IconButton
                                  style={{ color: themeColor }}
                                  onClick={() => handleViewClick(row)}
                                  size="small"
                                >
                                  <MdVisibility />
                                </IconButton>
                                <IconButton
                                  style={{ color: themeColor }}
                                  onClick={() => handleDownloadClick(row)}
                                  size="small"
                                >
                                  <MdDownload />
                                </IconButton>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="text-center">No records found</td>
                          </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                <div className="d-flex justify-content-end mt-3">
                  <div className="pagination">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleFirstPage}
                      disabled={currentPage === 1 || loading}
                      className="me-2"
                    >
                      First
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handlePrevPage}
                      disabled={currentPage === 1 || loading}
                      className="me-2"
                    >
                      Prev
                    </Button>
                    <span className="mx-2 align-self-center">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages || loading}
                      className="me-2"
                    >
                      Next
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleLastPage}
                      disabled={currentPage === totalPages || loading}
                    >
                      Last
                    </Button>
                  </div>
                </div>

                {selectedFile && (
                  <div>
                    {/* <div className="row justify-content-between align-items-center mb-3">
                      <div className="col-auto">
                        <h6>Preview - {selectedFile}</h6>
                      </div>
                      <div className="col-auto">
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleDownloadClick(selectedFile)}
                          style={{ backgroundColor: "var(--theme-color)", color: "#fff" }}
                          size="small"
                        >
                          Download
                        </Button>
                      </div>
                    </div> */}
                    <PreviewTable
                      previewTableData={previewTableData}
                      loading={isPreviewLoading}
                    //   onExport={() => handleDownloadClick(selectedFile)}
                    />
                  </div>
                )}
            </div>
        </div>
    );
};

export default History;
